import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrg } from '../../context/OrgContext';
import { useAuth } from '../../context/AuthContext';
import CreateOrganization from './CreateOrganization';

const OrganizationSelector: React.FC = () => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { organizations, memberships, loading, error, maxOrganizations, canCreateMore } = useOrg();
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();

  const handleSelectOrganization = (urlName: string) => {
    navigate(`/${urlName}`, { replace: true });
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };

  const getRoleForOrganization = (orgId: number): { role: string; onBehalfOf?: string } => {
    const membership = memberships.find(m => m.orgId === orgId);
    return {
      role: membership?.role || 'member',
      onBehalfOf: membership?.onBehalfOf
    };
  };

  const formatDate = (dateString: string): string => {
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'Unknown';
    }
  };

  // Filter organizations based on search query
  const filteredOrganizations = organizations.filter((org) => {
    if (!searchQuery.trim()) return true;
    
    const query = searchQuery.toLowerCase();
    const membershipInfo = getRoleForOrganization(org.id);
    
    return (
      org.name.toLowerCase().includes(query) ||
      org.urlName.toLowerCase().includes(query) ||
      (membershipInfo.onBehalfOf && membershipInfo.onBehalfOf.toLowerCase().includes(query))
    );
  });

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner-container">
          <div className="loading-spinner-text">Loading organizations...</div>
          <div className="loading-spinner-element"></div>
        </div>
      </div>
    );
  }

  if (showCreateForm || organizations.length === 0) {
    return <CreateOrganization />;
  }

  return (
    <div className="org-selector-container">
      <div className="org-selector-card">
        {/* Header */}
        <div className="org-selector-header">
          <h1>Select Your Cafe</h1>
          <p>Choose which cafe you'd like to manage</p>
          <div style={{ marginTop: '24px' }}>
            <button 
              onClick={handleLogout}
              className="btn btn-secondary btn-small"
            >
              Logout ({currentUser?.email?.split('@')[0]})
            </button>
          </div>
        </div>

        {error && (
          <div className="error-message" style={{ marginBottom: '32px' }}>
            {error}
          </div>
        )}

        {/* Search Bar */}
        <div className="org-search-container">
          <div className="org-search-input-wrapper">
            <input
              type="text"
              placeholder="Search cafes by name, slug, or owner..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="org-search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="org-search-clear"
                title="Clear search"
              >
                ✕
              </button>
            )}
          </div>
          {searchQuery && (
            <div className="org-search-results">
              Found {filteredOrganizations.length} of {organizations.length} cafes
            </div>
          )}
        </div>

        {/* Organizations Grid */}
        <div className="orgs-grid">
          {filteredOrganizations.map((org) => {
            const membershipInfo = getRoleForOrganization(org.id);
            const isOwner = currentUser?.uid === org.createdBy;
            
            return (
              <div 
                key={org.id} 
                className="org-card"
              >
                <div className="org-card-content" onClick={() => handleSelectOrganization(org.urlName)}>
                  <div className="org-card-header">
                    <h3>{org.name}</h3>
                    <span className="org-card-url">/{org.urlName}</span>
                  </div>
                  
                  <div className="org-card-meta">
                    <span>Created {formatDate(org.createdTime)}</span>
                    <div className="org-card-role-container">
                      <span className={`org-card-role ${membershipInfo.role}`}>
                        {membershipInfo.role}
                      </span>
                      {membershipInfo.onBehalfOf && membershipInfo.onBehalfOf !== currentUser?.email && (
                        <span className="org-card-on-behalf">
                          on behalf of {membershipInfo.onBehalfOf}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {isOwner && (
                  <div className="org-card-actions">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/${org.urlName}/settings`);
                      }}
                      className="btn btn-secondary btn-small"
                      title="Organization Settings"
                    >
                      ⚙️ Settings
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Create New Cafe Option */}
        <div className="create-org-action">
          <div className="cafe-count-info">
            <p>You have {organizations.length} of {maxOrganizations} cafe{maxOrganizations > 1 ? 's' : ''}</p>
          </div>
          {canCreateMore ? (
            <>
              <p>Want to create your cafe?</p>
              <button 
                onClick={() => setShowCreateForm(true)}
                className="btn btn-primary"
              >
                Create New Cafe
              </button>
            </>
          ) : (
            <div className="limit-reached">
              <p>You've reached the maximum of {maxOrganizations} cafe{maxOrganizations > 1 ? 's' : ''}.</p>
              <p className="limit-hint">
                Need more cafes? <a href="/contact" style={{ color: '#007AFF', textDecoration: 'underline' }}>Contact us</a> to discuss your needs.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrganizationSelector; 